import { SxProps, Theme } from "@mui/material";

export const darkInput: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.05)",
    borderRadius: "12px",
    color: "white",
    fontFamily: "'Barlow', sans-serif",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(32,194,241,0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#20C2F1" },
    "&.Mui-disabled": {
      color: "white",
      "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
      "& input": {
        WebkitTextFillColor: "white",
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.5)",
    fontFamily: "'Barlow', sans-serif",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#20C2F1" },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "rgba(255,255,255,0.5)",
  },
};

export const ctaButton: SxProps<Theme> = {
  background: "linear-gradient(90deg, #f97316, #fb923c)",
  borderRadius: "50px",
  py: 1.75,
  fontSize: "1rem",
  fontWeight: 700,
  fontFamily: "'Barlow', sans-serif",
  textTransform: "none",
  boxShadow: "0 4px 24px rgba(249,115,22,0.35)",
  "&:hover": { background: "linear-gradient(90deg, #ea6c0a, #f97316)" },
};

export const gradientText: SxProps<Theme> = {
  background: "linear-gradient(90deg, #38bdf8 0%, #20C2F1 55%, #67e8f9 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

export const eyebrow: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "0.72rem",
  fontWeight: 600,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: "#20C2F1",
  mb: 1.5,
};

export const stepIndicatorWrapper: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 6,
  gap: 0,
};

export const stepNumberCircle = (isComplete: boolean, isActive: boolean): SxProps<Theme> => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: isComplete || isActive ? "#20C2F1" : "rgba(255,255,255,0.08)",
  border: isComplete || isActive ? "none" : "1px solid rgba(255,255,255,0.15)",
  transition: "all 0.3s ease",
});

export const stepNumberText = (isActive: boolean): SxProps<Theme> => ({
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: "0.85rem",
  color: isActive ? "#040922" : "rgba(255,255,255,0.3)",
});

export const stepLabel = (isComplete: boolean, isActive: boolean): SxProps<Theme> => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: "0.65rem",
  fontWeight: 500,
  letterSpacing: "0.05em",
  color: isComplete || isActive ? "#20C2F1" : "rgba(255,255,255,0.3)",
  transition: "color 0.3s ease",
});

export const stepConnector = (isBeforeCurrent: boolean): SxProps<Theme> => ({
  height: "1px",
  width: 60,
  background: isBeforeCurrent ? "#20C2F1" : "rgba(255,255,255,0.12)",
  mb: 2.5,
  mx: 1,
  transition: "background 0.3s ease",
});

export const stepHeading: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  mb: 1,
  color: "white",
};

export const stepSubtext: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: "1rem",
  mb: 4,
};

export const step2Heading: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  mb: 4,
  color: "white",
};

export const planCard: SxProps<Theme> = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(56,189,248,0.14)",
  borderRadius: "20px",
  backdropFilter: "blur(16px)",
  p: { xs: 3, md: 4 },
  mb: 4,
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
};

export const planPrice: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: "3rem",
  lineHeight: 1,
  mb: 0.5,
  background: "linear-gradient(90deg, #38bdf8 0%, #20C2F1 55%, #67e8f9 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

export const planName: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  fontSize: "1rem",
  color: "rgba(255,255,255,0.82)",
  mb: 3,
};

export const planBulletList: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  textAlign: "left",
  maxWidth: 320,
  mx: "auto",
};

export const bulletCheckCircle: SxProps<Theme> = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "rgba(32,194,241,0.12)",
  border: "1px solid rgba(32,194,241,0.4)",
  boxShadow: "0 0 10px rgba(32,194,241,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const bulletText: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: "0.95rem",
};

export const cardNumberInput: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.05)",
    borderRadius: "12px",
    color: "white",
    fontFamily: "'Barlow', sans-serif",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(32,194,241,0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#20C2F1" },
    "& input::placeholder": { color: "rgba(255,255,255,0.25)", opacity: 1 },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.5)",
    fontFamily: "'Barlow', sans-serif",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#20C2F1" },
};

export const paymentDisclaimer: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.82rem",
  color: "rgba(255,255,255,0.45)",
  textAlign: "center",
  mb: 1,
};

export const backButton: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.5)",
  textTransform: "none",
  fontSize: "0.9rem",
  "&:hover": { color: "#20C2F1", background: "transparent" },
};

export const step3Heading: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  mb: 1,
  textAlign: "center",
  color: "white",
};

export const step3Subtext: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: "1rem",
  mb: 4,
  textAlign: "center",
};

export const passwordHint: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.82rem",
  color: "rgba(255,255,255,0.4)",
  mb: 0.5,
};

export const passwordRuleList: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
};

export const stripeFieldLabel: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.85rem",
  color: "rgba(255,255,255,0.5)",
  mb: 0.75,
};

export const stripeElementWrapper: SxProps<Theme> = {
  background: "rgba(255,255,255,0.05)",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.12)",
  px: 2,
  py: 1.75,
  transition: "border-color 0.2s ease",
  "&:hover": {
    borderColor: "rgba(32,194,241,0.4)",
  },
  "&:focus-within": {
    borderColor: "#20C2F1",
  },
};

export const stripeErrorText: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.85rem",
  color: "#f87171",
  textAlign: "center",
};

export const successHeading: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: { xs: "1.6rem", md: "2rem" },
  mb: 1.5,
  background: "linear-gradient(90deg, #38bdf8 0%, #20C2F1 55%, #67e8f9 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

export const successSubtext: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: "1rem",
  mb: 4,
};

export const pageWrapper: SxProps<Theme> = {
  minHeight: "100vh",
  background: "#040922",
  position: "relative",
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

export const backgroundGradient: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background:
    "radial-gradient(ellipse at 20% 10%, rgba(56,189,248,0.07) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(32,194,241,0.06) 0%, transparent 60%)",
  pointerEvents: "none",
};

export const formCard: SxProps<Theme> = {
  background: "#0b1120",
  border: "1px solid rgba(56,189,248,0.12)",
  borderRadius: "20px",
  p: { xs: 3, md: 5 },
  position: "relative",
  overflow: "hidden",
};

export const backToJoinButton: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.35)",
  textTransform: "none",
  fontSize: "0.85rem",
  px: 0,
  "&:hover": { color: "#20C2F1", background: "transparent" },
};

export const googleButton: SxProps<Theme> = {
  width: "100%",
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.25)",
  color: "white",
  borderRadius: "12px",
  py: 1.5,
  mb: 2.5,
  gap: 1.5,
  fontFamily: "'Barlow', sans-serif",
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  "&:hover": {
    background: "rgba(32,194,241,0.06)",
    borderColor: "rgba(32,194,241,0.4)",
  },
};

export const orDivider: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  my: 3,
  gap: 1.5,
  "&::before, &::after": {
    // eslint-disable-next-line quotes
    content: '""',
    flex: 1,
    height: "1px",
    background: "rgba(255,255,255,0.3)",
  },
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.85rem",
  color: "rgba(255,255,255,0.5)",
};

export const googleConnectedBanner: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  background: "rgba(32,194,241,0.06)",
  border: "1px solid rgba(32,194,241,0.25)",
  borderRadius: "12px",
  px: 2,
  py: 1.5,
  mb: 3,
};

export const googleConnectedIconWrap: SxProps<Theme> = {
  width: 32,
  height: 32,
  borderRadius: "50%",
  background: "rgba(32,194,241,0.12)",
  border: "1px solid rgba(32,194,241,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const googleConnectedTitle: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  fontSize: "0.95rem",
  color: "white",
  lineHeight: 1.2,
};

export const googleConnectedSubtitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.85rem",
  color: "rgba(255,255,255,0.6)",
  lineHeight: 1.3,
};
